import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogTitle, Typography } from "@mui/material";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { BusinessMessageRequest, BusinessMessageSearchCriteria } from "../../../../service/model/message.model";
import { addDays } from "date-fns";
import BusinessMessageTable from "./business-message-table";
import * as messageSlice from "../../business-message.slice";
import { Business } from "../../../../service/model/business.model";
import { BusinessMessageType, MessageChannel } from "../../../../shared/enums";
import FormField from "../../../../components/form-controls/form-field";
import Grid from "@mui/material/Grid";
import theme from "../../../../app/app-theme";
import FormCloseButton from "../../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { FormSelectField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";

interface BusinessMessageModalProps {
    readonly business: Business
    readonly onClose: () => void
}

const mapMessageType = (type: string): string => {
    if (type === BusinessMessageType.PAYMENT_REMINDER) {
        return "payment_reminder";
    } else if (type === BusinessMessageType.GENERAL_ANNOUNCEMENT) {
        return "general_announcement";
    } else {
        return "";
    }
}

const style = {
    p: 2,
};

const validationSchema = yup.object().shape({
    messageType: yup.string().required("Type is required"),
    messageContent: yup.string(),
});

export type FormValues = yup.InferType<typeof validationSchema>;

const BusinessMessageModal: React.FC<BusinessMessageModalProps> = ({ business, onClose }) => {
    const dispatch = useAppDispatch();
    const messages = useAppSelector(messageSlice.selectMessages);
    const { register, watch, setValue, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const messageType = watch("messageType");
    const messageText = watch("messageContent");

    const onSubmitHandler = (data: any) => {
        const request = {
            businessId: business.id,
            messageType: mapMessageType(data.messageType),
            messageContent: data.messageContent,
            channel: MessageChannel.SMS
        } as BusinessMessageRequest
        dispatch(messageSlice.sendMessageToBusiness(request))
        onClose();
    };

    // const getRegister = (name: keyof FormValues, validation?: any): UseFormRegisterReturn => {
    //     return register(name, validation);
    // };

    useEffect(() => {
        const searchCriteria = {
            businessId: business.id,
            pagination: {
                page: 0,
                size: 10
            },
            from: addDays(new Date(), -30).toISOString().slice(0, 10),
            to: new Date().toISOString().slice(0, 10),
        } as BusinessMessageSearchCriteria;
        dispatch(messageSlice.fetchBusinessMessagePage(searchCriteria));
    }, []);


    const onClear = () => {
        setValue("messageContent", "");
    }

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth="md">
            <DialogTitle>Message Details</DialogTitle>

            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <FormField>
                            <FormSelectField
                                id={"type"}
                                label="Message Type"
                                name="messageType"
                                defaultValue={"Payment Reminder"}
                                errorMessage={errors.messageType?.message}
                                options={Object.values(BusinessMessageType).map(type => { return { label: type, value: type } })}
                                register={register}
                            />
                        </FormField>
                        {messageType === BusinessMessageType.GENERAL_ANNOUNCEMENT &&
                            <>
                                <FormField>
                                    <FormTextField
                                        id="content"
                                        label="Message Content"
                                        name="messageContent"
                                        multiline={true}
                                        variant="outlined"
                                        errorMessage={errors.messageContent?.message}
                                        register={register}
                                    />
                                </FormField>
                                <Typography sx={{pl: 2}} variant="caption">Characters: {messageText?.length}  |  Message Count: {Math.ceil((messageText?.length ?? 0)/160)}  |  Remaining: {160 - ((messageText?.length ?? 0)%160)}</Typography>
                            </>
                        }
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClear}>Clear</Button>
                            <Button type="submit" variant="contained" >Send</Button>
                        </Grid>
                    </form>
                </>
                <BusinessMessageTable businessId={business.id} messages={messages} />
            </Box>
        </Dialog>
    );
}

export default BusinessMessageModal;